/**
 * THE WORDS OF THE SELLING PAGE, IN THE THREE LANGUAGES THE HOUSE SPEAKS.
 *
 * „Trei limbi, ca meniul. Un oaspete din Kiev cumpără în rusă sau nu cumpără"
 * (REZERVARI.md §1.5). The UNIT's own words — its name, its description, what is
 * included — come from the server in the guest's language; these are the
 * page's own furniture, which no house should have to type.
 *
 * THE REFUSALS ARE FUNCTIONS, NOT STRINGS, and that is the whole reason the API
 * answers with `{ code, need }` rather than a sentence: „minim 2 nopți" needs the
 * 2, „sosirea doar duminica, marțea" needs the list, and a server-rendered
 * Romanian sentence would leave a Russian guest reading a rule they cannot
 * follow. Same contract a WordPress plugin will use in whatever language its own
 * site speaks.
 */

export type Refusal =
  | { code: "not_for_sale" }
  | { code: "past" }
  | { code: "min_nights"; need: number }
  | { code: "max_nights"; limit: number }
  | { code: "too_soon"; need: number }
  | { code: "too_far"; limit: number }
  | { code: "arrival_day"; allowed: number[] }
  | { code: "departure_day"; allowed: number[] }
  | { code: "min_guests"; need: number }
  | { code: "max_guests"; limit: number };

export const LANGS = [["ro", "RO"], ["ru", "RU"], ["en", "EN"]] as const;

/** 0 = Sunday, the numbering `arrival_days` stores and `getUTCDay()` returns. */
const WEEKDAYS = {
  ro: ["duminică", "luni", "marți", "miercuri", "joi", "vineri", "sâmbătă"],
  ru: ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
} as const;

const DICT = {
  ro: {
    title: "Cazare și spații",
    checkIn: "Check In",
    checkOut: "Check Out",
    guests: "Persoane",
    check: "Verifică disponibilitatea",
    checking: "Verificăm…",
    more: "Vezi mai multe",
    back: "Înapoi",
    perNight: "noapte",
    perDay: "zi",
    from: "De la",
    sleeps: "Dormi",
    upTo: "până la",
    people: "persoane",
    free: "Liber",
    taken: "Ocupat în perioada aleasă",
    pickDates: "Alege datele ca să vezi prețul",
    pickArrival: "Alege ziua sosirii",
    pickDeparture: "Acum ziua plecării - după",
    weekdayShort: "L,Ma,Mi,J,V,S,D",
    chosen: "ales",
    busy: "ocupat",
    freeDay: "liber",
    closedDay: "Casa nu începe/încheie un sejur în ziua asta",
    tooShort: "Prea scurt sau prea lung pentru regulile casei",
    prevMonth: "Luna trecută",
    nextMonth: "Luna viitoare",
    nightsWord: "nopți",
    changeDates: "Schimbă datele",
    included: "Ce este inclus",
    rules: "Reguli ale casei",
    nights: "nopți",
    night: "noapte",
    total: "Total",
    approx: "aprox.",
    book: "Rezervă",
    booking: "Se rezervă…",
    yourDetails: "Datele tale",
    name: "Nume",
    email: "Email",
    phone: "Telefon",
    note: "Mesaj pentru gazdă",
    optional: "opțional",
    nameNeeded: "Scrie numele tău.",
    emailNeeded: "Scrie un email valid - acolo primești confirmarea.",
    held: "Rezervare înregistrată",
    confirmed: "Rezervare confirmată",
    lapsed: "Rezervarea nu mai este activă",
    lapsedBody: "Datele nu mai sunt ținute pentru tine. Alege din nou perioada.",
    heldFor: "Ținem datele pentru tine",
    minutes: "minute",
    whatNext: "Ce urmează",
    whatNextBody:
      "Gazda confirmă rezervarea și te caută la datele de contact lăsate. Plata online pentru rezervări nu este încă deschisă - nu ți s-a luat niciun ban.",
    yourCode: "Codul rezervării",
    keepCode: "Păstrează acest link ca să revii la rezervare.",
    somebodyTook: "Cineva tocmai a luat aceste date. Alege altele.",
    failed: "Nu am putut înregistra rezervarea. Încearcă din nou.",
    noUnits: "Casa nu are nimic de închiriat acum.",
    notFound: "Nu găsim ce cauți.",
    photo: "Fotografia",
    of: "din",
  },
  ru: {
    title: "Проживание и площадки",
    checkIn: "Заезд",
    checkOut: "Выезд",
    guests: "Гостей",
    check: "Проверить наличие",
    checking: "Проверяем…",
    more: "Подробнее",
    back: "Назад",
    perNight: "ночь",
    perDay: "день",
    from: "От",
    sleeps: "Спальных мест",
    upTo: "до",
    people: "человек",
    free: "Свободно",
    taken: "Занято в выбранные даты",
    pickDates: "Выберите даты, чтобы увидеть цену",
    pickArrival: "Выберите день заезда",
    pickDeparture: "Теперь день выезда - после",
    weekdayShort: "Пн,Вт,Ср,Чт,Пт,Сб,Вс",
    chosen: "выбрано",
    busy: "занято",
    freeDay: "свободно",
    closedDay: "В этот день заезд/выезд не принимается",
    tooShort: "Слишком коротко или слишком долго по правилам дома",
    prevMonth: "Прошлый месяц",
    nextMonth: "Следующий месяц",
    nightsWord: "ночей",
    changeDates: "Изменить даты",
    included: "Что включено",
    rules: "Правила дома",
    nights: "ночей",
    night: "ночь",
    total: "Итого",
    approx: "прибл.",
    book: "Забронировать",
    booking: "Бронируем…",
    yourDetails: "Ваши данные",
    name: "Имя",
    email: "Email",
    phone: "Телефон",
    note: "Сообщение хозяйке",
    optional: "необязательно",
    nameNeeded: "Напишите ваше имя.",
    emailNeeded: "Укажите действующий email - туда придёт подтверждение.",
    held: "Заявка принята",
    confirmed: "Бронь подтверждена",
    lapsed: "Бронь больше не активна",
    lapsedBody: "Даты за вами не держатся. Выберите период заново.",
    heldFor: "Держим даты за вами",
    minutes: "минут",
    whatNext: "Что дальше",
    whatNextBody:
      "Хозяйка подтвердит бронь и свяжется с вами по указанным контактам. Онлайн-оплата брони пока не открыта - с вас ничего не списано.",
    yourCode: "Номер брони",
    keepCode: "Сохраните эту ссылку, чтобы вернуться к брони.",
    somebodyTook: "Эти даты только что заняли. Выберите другие.",
    failed: "Не удалось оформить бронь. Попробуйте ещё раз.",
    noUnits: "Сейчас нечего арендовать.",
    notFound: "Не нашли то, что вы ищете.",
    photo: "Фото",
    of: "из",
  },
  en: {
    title: "Stays and spaces",
    checkIn: "Check In",
    checkOut: "Check Out",
    guests: "Guests",
    check: "Check availability",
    checking: "Checking…",
    more: "See more",
    back: "Back",
    perNight: "night",
    perDay: "day",
    from: "From",
    sleeps: "Sleeps",
    upTo: "up to",
    people: "people",
    free: "Available",
    taken: "Taken on those dates",
    pickDates: "Pick your dates to see the price",
    pickArrival: "Choose the day you arrive",
    pickDeparture: "Now the day you leave - after",
    weekdayShort: "Mo,Tu,We,Th,Fr,Sa,Su",
    chosen: "chosen",
    busy: "taken",
    freeDay: "free",
    closedDay: "The house does not start or end a stay on this day",
    tooShort: "Too short or too long for the house rules",
    prevMonth: "Previous month",
    nextMonth: "Next month",
    nightsWord: "nights",
    changeDates: "Change dates",
    included: "What's included",
    rules: "House rules",
    nights: "nights",
    night: "night",
    total: "Total",
    approx: "approx.",
    book: "Book",
    booking: "Booking…",
    yourDetails: "Your details",
    name: "Name",
    email: "Email",
    phone: "Phone",
    note: "Message for the host",
    optional: "optional",
    nameNeeded: "Please write your name.",
    emailNeeded: "Please write a valid email - that is where the confirmation goes.",
    held: "Reservation recorded",
    confirmed: "Reservation confirmed",
    lapsed: "This reservation is no longer active",
    lapsedBody: "The dates are not being held for you. Please pick them again.",
    heldFor: "We are holding your dates",
    minutes: "minutes",
    whatNext: "What happens next",
    whatNextBody:
      "The host confirms your reservation and gets in touch using the details you left. Online payment for reservations is not open yet - nothing has been charged.",
    yourCode: "Reservation code",
    keepCode: "Keep this link to come back to your reservation.",
    somebodyTook: "Somebody just took those dates. Please pick others.",
    failed: "We could not record the reservation. Please try again.",
    noUnits: "There is nothing to rent at the moment.",
    notFound: "We cannot find what you are looking for.",
    photo: "Photo",
    of: "of",
  },
} as const;

export type RentLang = keyof typeof DICT;
/**
 * The SHAPE of the dictionary, with the values widened to `string`.
 *
 * `(typeof DICT)["ro"]` would have been the literal Romanian — so a component
 * typed with it could only ever be handed the Romanian, and `copy("ru")` would
 * not compile. Mapping the keys keeps the "every language has every key" check
 * (a missing Russian key is still a type error) without freezing the words.
 */
export type RentCopy = { [K in keyof (typeof DICT)["ro"]]: string };

export const isRentLang = (l: string): l is RentLang => l in DICT;
export const copy = (lang: string): RentCopy => DICT[isRentLang(lang) ? lang : "ro"];

/** „duminică, marțea, miercuri" — the days a stay may begin or end on. */
const daysInWords = (lang: RentLang, allowed: number[]): string =>
  allowed.map((d) => WEEKDAYS[lang][d] ?? "").filter(Boolean).join(", ");

/**
 * A refusal, said out loud.
 *
 * Every branch names the NUMBER the house set. „Nu se poate" tells a guest to
 * give up; „minim 2 nopți" tells them what to change, and the difference is a
 * sale.
 */
export function refusalText(lang: string, r: Refusal): string {
  const l: RentLang = isRentLang(lang) ? lang : "ro";
  const ro = l === "ro", ru = l === "ru";
  switch (r.code) {
    case "not_for_sale":
      return ro ? "Nu se închiriază separat."
        : ru ? "Не сдаётся отдельно."
        : "Not rented on its own.";
    case "past":
      return ro ? "Data a trecut."
        : ru ? "Эта дата уже прошла."
        : "That date has passed.";
    case "min_nights":
      return ro ? `Minim ${r.need} nopți.`
        : ru ? `Минимум ${r.need} ноч.`
        : `Minimum ${r.need} nights.`;
    case "max_nights":
      return ro ? `Maxim ${r.limit} nopți.`
        : ru ? `Максимум ${r.limit} ноч.`
        : `Maximum ${r.limit} nights.`;
    case "too_soon":
      return ro ? `Rezervările se fac cu cel puțin ${r.need} zile înainte.`
        : ru ? `Бронировать нужно минимум за ${r.need} дн.`
        : `Book at least ${r.need} days ahead.`;
    case "too_far":
      return ro ? `Se rezervă cu cel mult ${r.limit} de zile înainte.`
        : ru ? `Бронь открыта максимум за ${r.limit} дн.`
        : `Bookable at most ${r.limit} days ahead.`;
    case "arrival_day":
      return ro ? `Sosirea doar: ${daysInWords(l, r.allowed)}.`
        : ru ? `Заезд только: ${daysInWords(l, r.allowed)}.`
        : `Arrival only on: ${daysInWords(l, r.allowed)}.`;
    case "departure_day":
      return ro ? `Plecarea doar: ${daysInWords(l, r.allowed)}.`
        : ru ? `Выезд только: ${daysInWords(l, r.allowed)}.`
        : `Departure only on: ${daysInWords(l, r.allowed)}.`;
    case "min_guests":
      return ro ? `Pentru grupuri de la ${r.need} persoane.`
        : ru ? `Для групп от ${r.need} человек.`
        : `For groups of ${r.need} or more.`;
    case "max_guests":
      return ro ? `Maxim ${r.limit} persoane.`
        : ru ? `Максимум ${r.limit} человек.`
        : `Maximum ${r.limit} people.`;
  }
}

/**
 * Today and tomorrow in Chisinau, as the `min` of the two date fields.
 *
 * THE HOUSE'S DAY, NOT THE PHONE'S. A guest reading this in Berlin at 23.30 is
 * already on the next day in Pohrebea, and a picker that let them choose a date
 * the server would refuse as `past` is a form that fails on submit for a reason
 * it could have prevented.
 */
export function todayHere(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Chisinau",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * MONTH NAMES, WRITTEN OUT. `Intl.DateTimeFormat` would do this in one line and
 * in the guest's own locale — and that is exactly the problem: the locale here
 * is the LANGUAGE THE GUEST CHOSE on this page, not the one their phone is set
 * to. A guest from Kiev reading the Romanian page must see „septembrie".
 */
const MONTHS = {
  ro: ["ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
       "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"],
  ru: ["январь", "февраль", "март", "апрель", "май", "июнь",
       "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"],
  en: ["January", "February", "March", "April", "May", "June",
       "July", "August", "September", "October", "November", "December"],
} as const;

/**
 * „1 noapte", „2 nopți", „20 de nopți" — and the Russian is worse.
 *
 * The page printed „1 nopți", which is the kind of mistake that tells a guest
 * the site was not written by anyone who speaks to them. `worker/lib/core.ts`
 * already carries this rule for the evening's report (`roCount`) and explains
 * why it deserves a function: Romanian inserts „de" between a numeral and its
 * noun from twenty upward, and the rule RECURSES ON THE LAST TWO DIGITS — which
 * is why 101 loses it again and 100 keeps it. The guest bundle cannot import
 * from the Worker, so the rule lives here too, deliberately, rather than being
 * approximated with a plural „s".
 *
 * RUSSIAN HAS THREE FORMS, not two: 1 ночь, 2-4 ночи, 5-20 ночей, and then it
 * starts again — except 11-14, which take the many-form whatever their last
 * digit says. Getting this wrong is the single commonest tell of a translated
 * interface.
 */
export function nightsPhrase(lang: string, n: number): string {
  const l: RentLang = isRentLang(lang) ? lang : "ro";
  const last2 = n % 100;
  const last1 = n % 10;

  if (l === "ru") {
    const teens = last2 >= 11 && last2 <= 14;
    const word = teens ? "ночей" : last1 === 1 ? "ночь" : last1 >= 2 && last1 <= 4 ? "ночи" : "ночей";
    return `${n} ${word}`;
  }
  if (l === "en") return `${n} ${n === 1 ? "night" : "nights"}`;

  if (n === 1) return "1 noapte";
  const de = n !== 0 && (last2 === 0 || last2 >= 20);
  return `${n} ${de ? "de " : ""}nopți`;
}

/** „22 aug." — a date short enough to sit beside another one. */
export function shortDay(lang: string, date: string): string {
  const l: RentLang = isRentLang(lang) ? lang : "ro";
  const i = Number(date.slice(5, 7)) - 1;
  /*
   * A REAL ABBREVIATION LIST, not `slice(0, 4)`. Slicing produced „22 augu.",
   * which is not a word in any of the three languages — and „mai" would have
   * become „mai." with a full stop it does not want.
   */
  return `${Number(date.slice(8))} ${MONTHS_SHORT[l][i] ?? ""}`;
}

const MONTHS_SHORT = {
  ro: ["ian.", "feb.", "mar.", "apr.", "mai", "iun.",
       "iul.", "aug.", "sept.", "oct.", "noi.", "dec."],
  ru: ["янв.", "фев.", "мар.", "апр.", "мая", "июн.",
       "июл.", "авг.", "сен.", "окт.", "ноя.", "дек."],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
} as const;

/** „septembrie 2026" from „2026-09". */
export function monthName(lang: string, month: string): string {
  const l: RentLang = isRentLang(lang) ? lang : "ro";
  const i = Number(month.slice(5, 7)) - 1;
  return `${MONTHS[l][i] ?? ""} ${month.slice(0, 4)}`;
}

/**
 * 0 = Sunday, the numbering `arrival_days` stores server-side.
 *
 * UTC on a date-only string, the same decision worker/lib/booking-rules.ts
 * documents: the string IS the calendar day, and parsing it in local time lets a
 * DST boundary drag it into the previous one. The calendar and the server must
 * agree about which day is a Monday, or the page greys out the wrong column.
 */
export function weekdayOf(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

/** Whole days from `a` to `b`. */
export function daysBetween(a: string, b: string): number {
  return Math.round(
    (Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000
  );
}

/** Nights between two dates — arrival inclusive, departure exclusive. */
export function nightCount(from: string, to: string): number {
  if (!from || !to) return 0;
  const ms = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`);
  return ms > 0 ? Math.round(ms / 86_400_000) : 0;
}
